import React from "react";

import { capitalize } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";

import { ManifestJSONSchema } from "src/api/connectApi/manifestTypes";
import {
  useCreateSdkConfig,
  useUpdateSdkConfig,
} from "src/api/connectApi/queries";
import { FormItem } from "src/base-components/FormItem";
import {
  ProviderSdkCatalogEntry,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { Form } from "src/connections/config/manifest/common/Form";
import { ManifestFormType } from "src/connections/config/manifest/types";
import { getSdkBundleErrorDescription } from "src/connections/sdk/bundleErrors";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  /** Catalog entry providing the configuration schema. */
  entry: ProviderSdkCatalogEntry;
  /** Present = edit; `provider` and `provider_sdk_version` are immutable. */
  script?: ProviderSdkConfigResponse;
  baseUrl: string | undefined;
  onClose: () => void;
  onBack?: () => void;
};

/**
 * Schema-driven create/edit form for an SDK Script. The configuration fields
 * are rendered from the catalog entry's `config_schema` by the manifest form
 * renderer; the stored configuration shape equals the form shape, so edit
 * rehydration and submission are passthroughs. The provider/version-specific
 * validation is owned by the backend.
 */
export const SdkScriptConfigForm: React.FC<Props> = ({
  entry,
  script,
  baseUrl,
  onClose,
  onBack,
}) => {
  const formMethods = useForm<ManifestFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: script?.name ?? "",
      inputs: { sdk: script?.configuration ?? {} },
    },
  });
  const {
    formState: { errors, isSubmitting },
  } = formMethods;

  const createScript = useCreateSdkConfig(baseUrl);
  const updateScript = useUpdateSdkConfig(baseUrl);

  const onSubmit = async (data: ManifestFormType) => {
    const configuration = data.inputs.sdk as Record<string, unknown>;
    try {
      if (script) {
        await updateScript.mutateAsync({
          id: script.id,
          payload: { name: data.name, configuration },
        });
        toastActions.success({
          title: "SDK Script updated",
          description: `${data.name} was updated.`,
        });
      } else {
        await createScript.mutateAsync({
          provider: entry.provider,
          provider_sdk_version: entry.provider_sdk_version,
          name: data.name,
          configuration,
        });
        toastActions.success({
          title: "SDK Script created",
          description: `${data.name} was created.`,
        });
      }
      onClose();
    } catch (error) {
      toastActions.failure({
        title: script
          ? "Failed to update SDK Script"
          : "Failed to create SDK Script",
        description: getSdkBundleErrorDescription(
          error,
          "Please check the configuration and try again.",
        ),
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        data-loc="sdk-script-config-form"
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        <Modal.Content>
          <FormItem gap="sm" label="Name" isRequired>
            {Boolean(errors.name) && <ErrorHint>Name is required</ErrorHint>}
            <Input
              dataLoc="sdk-script-config-name"
              error={Boolean(errors.name)}
              placeholder={`e.g. ${capitalize(entry.provider)} Production`}
              {...formMethods.register("name", {
                validate: (value) =>
                  value.trim().length > 0 || "Name is required",
              })}
            />
          </FormItem>

          <div className="mb-4 rounded-lg bg-gray-50 px-4 pt-4 pb-9">
            <h3 className="mb-4 font-inter-normal-12px text-gray-600 uppercase">
              Configuration
            </h3>
            <Form
              formPath="inputs.sdk"
              prefix="sdk-script-config"
              schema={entry.config_schema as ManifestJSONSchema}
            />
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="sdk-script-config-save"
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
          secondaryButton={
            onBack && (
              <Button
                isDisabled={isSubmitting}
                variant="secondary"
                onClick={onBack}
              >
                Back
              </Button>
            )
          }
        />
      </form>
    </FormProvider>
  );
};
